/**
 * Card Component — shadcn/ui pattern
 *
 * Container de card reutilizável com variantes visuais.
 * Suporta dark mode via CSS variables e compound pattern.
 *
 * @module components/ui
 * @since 2025-12-15
 * @updated 2026-04-06 - Migrado para shadcn/ui (cn() + forwardRef)
 *
 * @example
 * // Card básico
 * <Card>
 *   <p>Conteúdo do card</p>
 * </Card>
 *
 * @example
 * // Card com header e footer (compound pattern)
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Título</CardTitle>
 *     <CardDescription>Subtítulo opcional</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     <p>Conteúdo principal</p>
 *   </CardContent>
 *   <CardFooter>
 *     <Button>Salvar</Button>
 *   </CardFooter>
 * </Card>
 *
 * @example
 * // Card clicável com hover effect
 * <Card hoverable onClick={() => navigate(`/item/${id}`)}>
 *   <h3>{item.name}</h3>
 * </Card>
 *
 * @example
 * // Card elevado para destaque
 * <Card variant="elevated" padding="lg">
 *   <StatCard title="Total de vendas" value={1234} />
 * </Card>
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Variante visual: default (sombra), elevated (mais sombra), outlined (borda), ghost (transparente) @default 'default' */
  variant?: 'default' | 'elevated' | 'outlined' | 'ghost';
  /** Padding interno: none, sm (8px), md (16px), lg (24px) @default 'md' */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Se true, adiciona efeito de hover com sombra e cursor pointer @default false */
  hoverable?: boolean;
}

const paddingClasses: Record<NonNullable<CardProps['padding']>, string> = {
  none: '',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-6',
};

const variantClasses: Record<NonNullable<CardProps['variant']>, string> = {
  default:
    'bg-card text-card-foreground shadow-md dark:shadow-lg border border-border',
  elevated:
    'bg-card text-card-foreground shadow-lg dark:shadow-xl border border-border',
  outlined:
    'bg-card text-card-foreground border border-border shadow-sm dark:shadow-none',
  ghost: 'bg-transparent dark:bg-transparent',
};

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'default',
      padding = 'md',
      hoverable = false,
      onClick,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        'rounded-lg transition-all duration-200',
        variantClasses[variant],
        paddingClasses[padding],
        hoverable &&
          'hover:shadow-lg dark:hover:shadow-xl hover:border-muted-foreground/40 cursor-pointer',
        onClick && 'cursor-pointer',
        className
      )}
      onClick={onClick}
      {...props}
    />
  )
);
Card.displayName = 'Card';

// ---------------------------------------------------------------------------
// CardHeader
// ---------------------------------------------------------------------------

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Elemento de ação (ex: botão) exibido à direita do título */
  action?: React.ReactNode;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, action, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center justify-between mb-4', className)}
      {...props}
    >
      <div className="font-semibold text-lg text-foreground">
        {children}
      </div>
      {action && <div>{action}</div>}
    </div>
  )
);
CardHeader.displayName = 'CardHeader';

// ---------------------------------------------------------------------------
// CardTitle
// ---------------------------------------------------------------------------

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight text-foreground',
      className
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

// ---------------------------------------------------------------------------
// CardDescription
// ---------------------------------------------------------------------------

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

// ---------------------------------------------------------------------------
// CardContent (shadcn alias) / CardBody (legado)
// ---------------------------------------------------------------------------

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

/** @deprecated Use CardContent instead */
const CardBody = CardContent;

// ---------------------------------------------------------------------------
// CardFooter
// ---------------------------------------------------------------------------

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'mt-4 pt-4 border-t border-border',
        className
      )}
      {...props}
    />
  )
);
CardFooter.displayName = 'CardFooter';

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardBody,
  CardFooter,
};
